<template>
  <div class="event-modal-passes-users">
    <template v-if="users.length">
      <Popup
        v-if="showDeleteAllMessage"
        @close="showDeleteAllMessage = false"
        @confirm="
          showDeleteAllMessage = false;
          $emit('delete-all');
        "
        remove
      >
        {{ $t("event.passes.deleteAllPopup") }}
      </Popup>
      <Popup
        v-if="showDeletePassMessage"
        @close="cancelDeletePass"
        @confirm="confirmDeletePass"
        remove
      >
        {{ $t("event.passes.deletePassPopup") }}
      </Popup>

      <div class="event-modal-passes-users__header" v-if="hasAccessToWrite">
        <LinkIcon
          :bold="true"
          icon="add"
          @click="$emit('add')"
          v-if="secondsToDeadline > 0 && allowAddingPass"
        >
          {{ $t("event.passes.addPass") }}
        </LinkIcon>

        <button
          class="event-modal-passes-users__header-remove-all"
          @click="$emit('upload')"
        >
          {{ $t("event.passes.reload") }}
        </button>
        <button
          class="event-modal-passes-users__header-remove-all last"
          @click="showDeleteAllMessage = true"
        >
          {{ $t("event.passes.deleteAll") }}
        </button>
      </div>
      <div
        v-if="isTypeOfParticipant || isInstallationType"
        class="event-modal-passes-users__sort"
      >
        <Dropdown
          :value="sorting"
          class="dropdown_slim dropdown_grey event-modal-passes-users__sort-dropdown"
          single
          @select="sortPasses"
          modified
          :first-word="locale === 'en' ? 'Sort' : 'Сортировка'"
        />
      </div>

      <template v-if="isTypeOfParticipant || isInstallationType">
        <div
          class="event-modal-passes-users__user"
          v-for="user in users"
          :key="user.id"
        >
          <div class="event-modal-passes-users__user-name">
            {{ combineUserName(user) }}<br />
            <span class="event-modal-passes-users__user-name--company">{{
              user.company_name
            }}</span>
          </div>
          <DotText
            v-if="user.status === 'rejected'"
            class="event-modal-passes-users__user-status _error"
          >
            <span
              v-if="user.type === 'visa'"
              class="event-modal-passes-users__user-status__text"
            >
              {{ $t("event.passes.errorVisaPass") }}
            </span>
            <span v-else class="event-modal-passes-users__user-status__text">{{
              $t("event.passes.errorBarcodeStatus")
            }}</span>
          </DotText>

          <DotText
            v-if="user.status === 'consideration'"
            class="event-modal-passes-users__user-status _warning"
          >
            <span class="event-modal-passes-users__user-status__text">{{
              $t("event.passes.processedStatus")
            }}</span>
          </DotText>
          <div class="event-modal-passes-users__user-actions">
            <template
              v-if="
                secondsFromDeadlinePrint < 0 &&
                  secondsToDeadlinePrint > 0 &&
                  user.status === 'accepted' &&
                  user.type !== 'visa'
              "
            >
              <button
                class="event-modal-passes-users__user-actions-action"
                @click="$emit('download', user)"
              >
                <Tooltip :label="$t('event.documents.downloadFileShort')">
                  <icon name="download-grey" />
                </Tooltip>
              </button>

              <button
                class="event-modal-passes-users__user-actions-action"
                @click="$emit('print', user)"
              >
                <Tooltip :label="$t('event.documents.printFile')">
                  <icon name="print-grey" />
                </Tooltip>
              </button>
            </template>

            <button
              class="event-modal-passes-users__edit event-modal-passes-users__user-actions-action"
              @click="
                $modal.show('event-modal-detail-pass', {
                  user
                })
              "
            >
              <tooltip :label="$t('event.documents.showFile')">
                <icon name="look-grey" />
              </tooltip>
            </button>
            <tooltip
              style="margin-right: 40px"
              v-if="
                (user.status === 'accepted' || user.status === 'rejected') &&
                  user.type === 'visa'
              "
            >
              <icon name="info" width="16" height="16" />
              <template #content>
                <p class="text-description">
                  <template v-if="user.status === 'accepted'"
                    >{{ $t("event.passes.participant.visaPassApplied") }}
                    <br />
                    {{ $t("event.passes.participant.visaPassAppliedContacts") }}
                  </template>
                  <template v-if="user.status === 'rejected'">
                    <p>{{ $t("event.passes.participant.visaPassRejected") }}</p>
                    <p>
                      {{
                        $t("event.passes.participant.visaPassRejected2", {
                          amount: user.country?.is_simple_visa ? 5 : 50
                        })
                      }}
                    </p>
                  </template>
                </p>
              </template>
            </tooltip>

            <button
              class="event-modal-passes-users__user-actions-action"
              @click="selectPassToBeDeleted(user)"
              v-if="
                hasAccessToWrite &&
                  (user.type !== 'visa' || user.status === 'rejected')
              "
            >
              <tooltip :label="$t('event.documents.deleteFile')">
                <icon name="delete" />
              </tooltip>
            </button>
          </div>
        </div>
      </template>

      <template v-if="isTypeOfAuto">
        <table class="event-modal-passes-users__car">
          <tbody>
            <tr>
              <th class="event-modal-passes-users__car-gvn-col">
                {{ $t("event.passes.car.govVehicleNumber") }}
              </th>

              <th class="event-modal-passes-users__car-vt-col">
                {{ $t("event.passes.car.vehicleType") }}
              </th>

              <th class="hidden-column event-modal-passes-users__car-gtn-col">
                {{ $t("event.passes.car.govTrailerNumber") }}
              </th>

              <th class="hidden-column event-modal-passes-users__car-ad-col">
                {{ $t("event.passes.car.arrivalDate") }}
              </th>

              <th class="event-modal-passes-users__car-actions-col">&nbsp;</th>
            </tr>
            <tr v-for="user in users" :key="user.id">
              <td>{{ user.car_number }}</td>
              <td>{{ findCarType(user.car_type) }}</td>
              <td class="hidden-column">{{ user.trailer_number || "—" }}</td>
              <td class="hidden-column">{{ formatDate(user.entry_date) }}</td>
              <td>
                <div class="event-modal-passes-users__car-actions">
                  <template v-if="user.status === 'accepted'">
                    <PassesUserAction
                      icon="passes-user-action-download"
                      class="event-modal-passes-users__car-actions-item"
                    >
                      <button @click="$emit('download', user)">
                        <icon
                          name="passes-user-action-download"
                          width="16"
                          height="16"
                        />
                        Скачать пропуск
                      </button>

                      <button @click="$emit('download-d03', user)">
                        <icon
                          name="passes-user-action-download"
                          width="16"
                          height="16"
                        />
                        Скачать форму D03
                      </button>
                    </PassesUserAction>

                    <PassesUserAction
                      icon="print-grey"
                      class="event-modal-passes-users__car-actions-item"
                    >
                      <button @click="$emit('print', user)">
                        <icon name="print-grey" width="16" height="16" />
                        Распечатать пропуск
                      </button>

                      <button @click="$emit('print-d03', user)">
                        <icon name="print-grey" width="16" height="16" />
                        Распечатать форму D03
                      </button>
                    </PassesUserAction>
                  </template>
                  <template v-else-if="user.status === 'rejected'">
                    <DotText class="_warning">
                      {{ $t("event.passes.car.rejectedStatus") }}

                      <div
                        v-if="user.comment"
                        class="event-modal-passes-users__car-declined"
                      >
                        <icon
                          name="info"
                          width="16"
                          height="16"
                          style="margin-left: 10px;"
                          class="event-modal-passes-users__car-declined-icon"
                        />

                        <div
                          class="event-modal-passes-users__car-declined-popup"
                        >
                          {{ user.comment }}
                        </div>
                      </div>
                    </DotText>
                  </template>
                  <template v-else-if="user.status === 'consideration'">
                    <DotText>
                      {{ $t("event.passes.processedStatus") }}
                    </DotText>
                  </template>

                  <button
                    class="button-clean event-modal-passes-users__car-actions-item"
                    @click="copyAutoPass(user)"
                    v-if="hasAccessToWrite"
                  >
                    <tooltip :label="$t('event.documents.copyFile')">
                      <icon class="copy" name="copy" width="16" height="16" />
                    </tooltip>
                  </button>

                  <button
                    class="button-clean event-modal-passes-users__car-actions-item"
                    @click="selectPassToBeDeleted(user)"
                    v-if="hasAccessToWrite"
                  >
                    <tooltip :label="$t('event.documents.deleteFile')">
                      <icon name="delete" width="16" height="16" />
                    </tooltip>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </template>

    <div
      v-else-if="secondsToDeadline < 0"
      class="event-modal-passes-users__header"
    >
      <p
        class="text-description"
        v-html="$t('event.passes.deadlineReachedAndNoPasses')"
      ></p>
    </div>
    <Loader v-if="isLoading" white opacity is-absolute />
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters, mapState } from "vuex";
import diffInSeconds from "@/helpers/diffInSeconds";
import { i18n } from "@v2/shared/config";

import PassesUserAction from "./PassesUserAction.vue";
import {
  Loader,
  Dropdown,
  LinkIcon,
  Tooltip,
  Popup,
  DotText
} from "@v2/shared/ui/components";

export default {
  name: "EventModalPassesUsers",

  components: {
    Loader,
    Popup,
    Dropdown,
    LinkIcon,
    DotText,
    PassesUserAction,
    Tooltip
  },

  props: {
    users: {
      type: Array,
      default: () => []
    },

    type: {
      type: String,
      validate(value) {
        return ["certificate", "car", "installation"].indexOf(value) > -1;
      }
    },

    deadline: {
      type: Object,
      default: () => moment()
    },

    deadlineStartPrint: {
      type: Object,
      default: () => moment()
    },

    deadlineEndPrint: {
      type: Object,
      default: () => moment()
    },

    allowAddingPass: {
      type: Boolean,
      default: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sorting: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    autoUserDownload: null,
    autoUserPrint: null,

    showDeleteAllMessage: false,

    showDeletePassMessage: false,
    passToBeDeleted: null
  }),

  computed: {
    ...mapGetters("event", ["hasAccessToWrite"]),
    ...mapState("passes", ["participantPassSorting"]),

    isTypeOfParticipant() {
      return this.type === "certificate";
    },

    isInstallationType() {
      return this.type === "installation";
    },

    isTypeOfAuto() {
      return this.type === "car";
    },

    secondsToDeadline() {
      if (!this.deadline) {
        return Infinity;
      }

      return diffInSeconds(this.deadline);
    },

    secondsFromDeadlinePrint() {
      if (!this.deadlineStartPrint) {
        return Infinity;
      }

      return diffInSeconds(this.deadlineStartPrint);
    },

    secondsToDeadlinePrint() {
      if (!this.deadlineEndPrint) {
        return Infinity;
      }

      return diffInSeconds(this.deadlineEndPrint);
    },
    locale() {
      return i18n.locale;
    }
  },

  methods: {
    copyAutoPass(user) {
      this.$emit("copyAutoPass", user);
    },
    combineUserName(user) {
      return [user.lastname, user.name, user.middlename].join(" ");
    },
    sortPasses(option) {
      if (this.isInstallationType) {
        this.$store.commit("passes/SET_MONTAGE_PASS_SORTING", option);
      }
      if (this.isTypeOfParticipant) {
        this.$store.commit("passes/SET_PERSONAL_PASS_SORTING", option);
      }

      this.$emit("sortPasses");
    },
    formatDate(date) {
      return moment(date).format("DD.MM.YYYY");
    },

    findCarType(carType) {
      return this.locale === "en" ? carType.title_eng : carType.title_rus;
    },

    selectPassToBeDeleted(pass) {
      this.passToBeDeleted = pass;
      this.showDeletePassMessage = true;
    },

    cancelDeletePass() {
      this.showDeletePassMessage = false;
      this.passToBeDeleted = null;
    },

    confirmDeletePass() {
      const pass = JSON.parse(JSON.stringify(this.passToBeDeleted));

      this.cancelDeletePass();
      this.$emit("delete", pass);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-passes-users {
  position: relative;
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    @include respond-down(md) {
      padding: 0 16px;
    }

    &-remove-all {
      @include button-clean();
      font-size: 12px;
      color: #a3a8b3;
      margin-left: auto;
      &.last {
        margin-left: 40px;
      }
    }
  }
  &__car-icon {
    @include respond-down(md) {
      display: none;
    }
  }
  &__sort {
    &-dropdown {
      display: inline-block;
    }

    margin-bottom: 1px;

    @include respond-down(md) {
      padding: 0 16px;
    }
  }

  &__user {
    display: flex;
    width: 100%;
    position: relative;
    align-items: center;

    border-top: 1px solid rgba(205, 206, 208, 0.5);
    border-bottom: 1px solid rgba(205, 206, 208, 0.5);

    margin-top: -1px;

    padding: 21px 0;

    @include respond-down(md) {
      box-sizing: border-box;
      padding: 19px 16px;
    }

    &-name {
      font-size: 16px;
      line-height: 1.5;
      color: #000000;

      flex-grow: 1;
      flex-shrink: 0;

      @include respond-down(md) {
        font-size: 13px;
      }

      &--company {
        font-size: 14px;
        color: #a3a8b3;
        display: block;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 500px;

        @include respond-down(md) {
          font-size: 11px;
          max-width: 300px;
        }

        @include respond-down(sm) {
          max-width: 280px;
        }

        @include respond-down(xs) {
          max-width: 200px;
        }
      }
    }

    &-status {
      margin-right: 10px;
      &__text {
        @include respond-down(xs) {
          display: none;
        }
      }
    }

    &-actions {
      margin-left: auto;
      display: flex;
      justify-content: flex-end;

      &-action {
        margin-right: 40px;
        position: relative;
        @include respond-down(md) {
          margin-right: 0;
          margin-left: 15px;
        }

        &:first-child {
          margin-left: auto;
        }

        &:last-child {
          margin-right: 20px;
        }

        @include button-clean();

        .svg-icon {
          width: 16px;
          height: 16px;
        }
      }
    }
  }

  &__car {
    width: 100%;

    border-spacing: 0;
    border-collapse: collapse;
    &-icon {
      position: relative;
      cursor: pointer;
      height: 12px;
      width: 12px;
      right: -6px;
    }
    tr {
      margin-top: -1px;
    }

    th,
    td {
      border-top: 1px solid rgba(205, 206, 208, 0.5);
      border-bottom: 1px solid rgba(205, 206, 208, 0.5);

      padding: 0 24px 0 0;
    }

    th {
      text-align: left;
      font-size: 12px;
      font-weight: normal;
      line-height: 1.33;
      color: #a3a8b3;

      padding: 15px 0;

      @include respond-down(md) {
        font-size: 11px;
        padding: 8px 16px;
      }
    }

    td {
      padding: 22px 5px 22px 0;
      font-size: 16px;
      color: #000000;

      @include respond-down(md) {
        font-size: 12px;
        padding: 17px 16px 17px 16px;
      }
    }

    &-actions {
      display: flex;
      justify-content: flex-end;

      text-align: right;
      padding-right: 0;

      @include respond-down(md) {
        padding-right: 0;
      }
      &-item {
        position: relative;
      }
      &-item:not(:first-child) {
        margin-left: 40px;

        @include respond-down(md) {
          margin-left: 15px;
        }
      }

      .dot-text {
        margin-right: auto;
        flex-shrink: 0;

        @include respond-down(md) {
          margin-right: 0;
          font-size: 0;
        }
      }
    }

    &-declined {
      position: relative;

      &-icon {
        flex-shrink: 0;
        cursor: pointer;
      }

      &-popup {
        @include description();
        font-size: 12px;

        position: absolute;

        right: 0;
        bottom: 30px;

        padding: 10px 15px;

        background: #fff;
        border: 1px solid #f1f1f1;

        width: 300px;

        opacity: 0;
        visibility: hidden;

        pointer-events: none;

        transition: all 0.2s;

        text-align: left;
      }

      &-icon:hover + &-popup {
        visibility: visible;
        opacity: 1;
      }
    }

    &-gvn-col {
      width: 129px;

      @include respond-down(md) {
        width: auto;
      }
    }

    &-vt-col {
      width: 125px;

      @include respond-down(md) {
        width: auto;
      }
    }

    &-gtn-col {
      width: 129px;
    }

    &-ad-col {
      width: 129px;
    }

    &-actions-col {
      width: 180px;
    }

    &-d03 {
      text-align: right;
      margin-top: 10px;
    }
  }

  .hidden-column {
    @include respond-down(md) {
      display: none;
    }
  }

  &__edit {
    padding: 0;
    border: none;
    box-shadow: none;
    outline: none;
    background-color: transparent;
    cursor: pointer;
    user-select: none;
    color: #767676;

    .svg-icon {
      width: 20px;
      height: 21px;
    }
  }
}
</style>
